import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

const DEFAULT_JOB_IMAGE = '/images/jobs/network.webp';
const MIN_IMAGE_HEIGHT = 160;

@customElement('ns-job-layout')
export class NSJobLayout extends LitElement {
  @property({ type: String })
  heading?: string;

  @property({ type: String })
  image?: string;

  @property({ type: String, attribute: 'image-label' })
  imageLabel = '';

  @query('.image')
  private __image?: HTMLElement;

  @query('.main')
  private __main?: HTMLElement;

  @query('.aside')
  private __aside?: HTMLElement;

  @state()
  private __imageFits = true;

  @state()
  private __mainScrolls = false;

  @state()
  private __asideScrolls = false;

  private readonly __resizeObserver = new ResizeObserver(() => this.__measure());

  connectedCallback() {
    super.connectedCallback();
    this.updateComplete.then(() => {
      [this, this.__image, this.__main, this.__aside].forEach((el) => el && this.__resizeObserver.observe(el));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.__resizeObserver.disconnect();
  }

  // The image fills what the aside leaves of its column, so it shows only under a short aside or none
  private __measure() {
    this.__imageFits = (this.__image?.clientHeight ?? 0) >= MIN_IMAGE_HEIGHT;
    this.__mainScrolls = !!this.__main && this.__main.scrollHeight > this.__main.clientHeight;
    this.__asideScrolls = !!this.__aside && this.__aside.scrollHeight > this.__aside.clientHeight;
  }

  // Watching the slotted content too, since a column's scroll height changes without the column resizing
  private __onSlotChange(ev: Event) {
    (ev.target as HTMLSlotElement).assignedElements().forEach((el) => this.__resizeObserver.observe(el));
  }

  private __onMainChange(ev: Event) {
    const elements = (ev.target as HTMLSlotElement).assignedElements();
    elements.forEach((element, index) => element.toggleAttribute('data-fill', index === elements.length - 1));
    this.__onSlotChange(ev);
  }

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--wa-space-m);
      height: 100%;
      container-type: inline-size;
    }

    :host([hidden]) {
      display: none;
    }

    h2 {
      margin: 0;
      font-size: var(--wa-font-size-l);
      font-weight: 500;
    }

    /* The columns fill the card and each scrolls on its own, so the heading and actions stay in view */
    .columns {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-template-rows: minmax(0, 1fr);
      gap: var(--wa-space-l);
      flex: 1 1 0;
      min-height: 0;
    }

    .main,
    .aside {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      /* Holds a column's absolutely positioned content (a screen-reader announcer) inside its own scroll; otherwise it's
         placed against the card and stretches the card's scroll with blank space */
      position: relative;

      /* Room inside the scroll edge for a focus ring (2px outline, 2px offset), without moving the content */
      padding: var(--wa-space-2xs);
      margin: calc(-1 * var(--wa-space-2xs));
    }

    .aside {
      gap: var(--wa-space-m);
    }

    .main:focus-visible,
    .aside:focus-visible {
      outline: 2px solid var(--wa-color-focus);
      outline-offset: -2px;
    }

    /* The column's last panel runs to its foot, level with the aside */
    ::slotted([data-fill]) {
      flex: 1 0 auto;
    }

    ::slotted([slot='intro']) {
      margin: 0 0 var(--wa-space-m);
      font-weight: 300;
    }

    .image {
      flex: 1 1 0;
      min-height: 0;
      border-radius: var(--ns-card-border-radius, 0.5em);
      background-repeat: no-repeat;
      background-size: var(--ns-job-layout-image-size, cover);
      background-position: var(--ns-job-layout-image-position, 0 0);
    }

    .image.unfitted {
      visibility: hidden;
    }

    /* Too narrow for two columns: stack them, scroll the card as one, and drop the image, which is only decoration */
    @container (max-width: 40rem) {
      .columns {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: none;
        flex: 1 0 auto;
      }
      .main,
      .aside {
        overflow-y: visible;
      }
      .image {
        display: none;
      }
    }
  `;

  render() {
    return html`
      <slot name="heading">
        ${this.heading
          ? html`
              <h2 part="heading">${this.heading}</h2>
            `
          : nothing}
      </slot>
      <div class="columns" part="columns">
        <!-- A column that scrolls takes focus, so it can be scrolled from the keyboard -->
        <div
          class="main"
          part="main"
          tabindex=${this.__mainScrolls ? 0 : nothing}
          role=${this.__mainScrolls ? 'region' : nothing}
          aria-label=${this.__mainScrolls ? 'Job' : nothing}
        >
          <slot name="intro" @slotchange=${this.__onSlotChange}></slot>
          <slot @slotchange=${this.__onMainChange}></slot>
        </div>
        <div
          class="aside"
          part="aside"
          tabindex=${this.__asideScrolls ? 0 : nothing}
          role=${this.__asideScrolls ? 'region' : nothing}
          aria-label=${this.__asideScrolls ? 'More information' : nothing}
        >
          <slot name="aside" @slotchange=${this.__onSlotChange}></slot>
          <div
            class=${classMap({ image: true, unfitted: !this.__imageFits })}
            role=${this.imageLabel && this.__imageFits ? 'img' : 'presentation'}
            aria-label=${(this.__imageFits && this.imageLabel) || nothing}
            style=${styleMap({ backgroundImage: `url(${this.image ?? DEFAULT_JOB_IMAGE})` })}
          ></div>
        </div>
      </div>
    `;
  }
}
